import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Panel, Tag } from "@/components/System";
import { DOCS } from "@/data/content";

const Docs = () => {
    const [active, setActive] = useState("overview");
    const [query, setQuery] = useState("");
    const doc = DOCS.find((d) => d.id === active);

    const filtered = useMemo(
        () => DOCS.filter((d) => d.title.toLowerCase().includes(query.toLowerCase())),
        [query]
    );

    return (
        <div>
            <Seo path="/docs" />
            <section className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6">
                <div className="vm-micro mb-6">AGENTVM://DOCS</div>
                <h1 className="text-3xl font-bold tracking-tight text-[#F5F9FF] sm:text-5xl">
                    TECHNICAL <span className="text-[#00D9FF] vm-glow">DOCUMENTATION.</span>
                </h1>

                <div className="mt-10 grid gap-8 lg:grid-cols-[260px_1fr]">
                    <aside className="lg:sticky lg:top-24 lg:self-start">
                        <input
                            data-testid="docs-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="FILTER SECTIONS"
                            aria-label="Filter documentation sections"
                            className="mb-3 w-full border border-[rgba(0,217,255,0.2)] bg-[#080F18] px-3 py-2 font-mono text-[11px] tracking-[0.14em] text-[#F5F9FF] placeholder:text-[#B8C7D9]/40 focus:border-[#00D9FF]/60 focus:outline-none"
                        />
                        <nav className="flex flex-col border-l border-[rgba(0,217,255,0.15)]" aria-label="Documentation sections">
                            {filtered.map((d) => (
                                <button
                                    key={d.id}
                                    data-testid={`docs-nav-${d.id}`}
                                    onClick={() => setActive(d.id)}
                                    aria-pressed={active === d.id}
                                    className={`px-4 py-2 text-left font-mono text-[11px] tracking-[0.16em] transition-colors ${
                                        active === d.id
                                            ? "border-l-2 border-[#00D9FF] bg-[#00D9FF]/[0.06] text-[#00D9FF]"
                                            : "text-[#B8C7D9]/70 hover:text-[#F5F9FF]"
                                    }`}
                                >
                                    {d.title.toUpperCase()}
                                </button>
                            ))}
                        </nav>
                    </aside>

                    <div>
                        <div className="vm-micro-dim mb-4" data-testid="docs-breadcrumb">
                            docs://agentvm <span className="text-[#00D9FF]/60">/</span> <span className="text-[#00D9FF]">{doc.id}</span>
                        </div>
                        <Panel title={doc.path} right={<Tag tone="beta">REFERENCE</Tag>} className="scanlines" testid="docs-content">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={doc.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className="p-6 sm:p-8"
                                >
                                    <h2 className="text-2xl font-semibold tracking-tight text-[#F5F9FF] sm:text-3xl">{doc.title}</h2>
                                    <div className="mt-5 space-y-4">
                                        {doc.body.map((p, i) => (
                                            <p key={i} className="max-w-2xl text-sm leading-relaxed text-[#B8C7D9] sm:text-base">
                                                {p}
                                            </p>
                                        ))}
                                    </div>
                                    {doc.code && (
                                        <pre className="mt-6 overflow-x-auto border border-[rgba(0,217,255,0.18)] bg-[#05070B] p-4 font-mono text-xs leading-6 text-[#00D9FF]/90" data-testid="docs-code">
                                            {doc.code}
                                        </pre>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </Panel>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Docs;
